import { supportChatLogger } from '../../logger'
import { SupportChatActions, SupportChatSeller } from '../../types'
import {
  getZendeskContextFieldIds,
  getZendeskEnvironment,
  getZendeskSnippetUrl,
} from './config'
import { buildZendeskContextFields } from './contextFields'
import { loadZendeskSnippet } from './loader'
import { addHiddenPrintClassToZendeskWidget } from './widget'

type CreateZendeskProviderParams = {
  seller: SupportChatSeller
  getJwt: () => Promise<string>
}

let zendeskLoginRegistered = false
let zendeskContextSignature: string | null = null
let inFlightZendeskJwtPromise: Promise<string> | null = null

export const resetZendeskProviderStateForTests = () => {
  zendeskLoginRegistered = false
  zendeskContextSignature = null
  inFlightZendeskJwtPromise = null
}

const getSellerContextSignature = (seller: SupportChatSeller) =>
  JSON.stringify({
    id: seller.id ?? '',
    document: seller.document ?? '',
    name: seller.name ?? '',
    city: seller.address?.city ?? '',
  })

const sendZendeskContext = (seller: SupportChatSeller) => {
  const fields = buildZendeskContextFields(seller, getZendeskContextFieldIds())
  if (!fields.length) return

  const signature = getSellerContextSignature(seller)
  if (zendeskContextSignature === signature) return

  globalThis.zE?.('messenger:set', 'conversationFields', fields)
  zendeskContextSignature = signature
  supportChatLogger.info('support_chat_context_sent', {
    provider: 'zendesk',
    field_count: fields.length,
  })
}

const loginZendeskUser = (getJwt: () => Promise<string>) => {
  if (zendeskLoginRegistered) return

  globalThis.zE?.(
    'messenger',
    'loginUser',
    async (callback: (jwt: string) => void) => {
      try {
        inFlightZendeskJwtPromise ??= getJwt().finally(() => {
          inFlightZendeskJwtPromise = null
        })

        const jwt = await inFlightZendeskJwtPromise
        callback(jwt)
        supportChatLogger.info('support_chat_login_succeeded', {
          provider: 'zendesk',
        })
      } catch (error) {
        supportChatLogger.error('support_chat_login_failed', {
          provider: 'zendesk',
          error:
            error instanceof Error
              ? error.message
              : 'unknown_zendesk_login_error',
        })
      }
    }
  )
  zendeskLoginRegistered = true
}

export const createZendeskProvider = ({
  seller,
  getJwt,
}: CreateZendeskProviderParams): SupportChatActions => ({
  load: async () => {
    const snippetUrl = getZendeskSnippetUrl()

    try {
      await loadZendeskSnippet(snippetUrl)
      addHiddenPrintClassToZendeskWidget()
      supportChatLogger.info('support_chat_snippet_loaded', {
        provider: 'zendesk',
        environment: getZendeskEnvironment(),
      })
      loginZendeskUser(getJwt)
      sendZendeskContext(seller)
    } catch (error) {
      supportChatLogger.error('support_chat_snippet_failed', {
        provider: 'zendesk',
        error:
          error instanceof Error ? error.message : 'unknown_zendesk_load_error',
      })
    }
  },
  open: () => {
    globalThis.zE?.('messenger', 'open')
  },
  disable: () => {
    addHiddenPrintClassToZendeskWidget()
    globalThis.zE?.('messenger', 'hide')
  },
  enable: () => {
    globalThis.zE?.('messenger', 'show')
  },
  logout: () => {
    globalThis.zE?.('messenger', 'logoutUser')
    zendeskLoginRegistered = false
    zendeskContextSignature = null
    inFlightZendeskJwtPromise = null
  },
})
